import React, { FC, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import useLocales from 'hooks/useLocales';
import EscoSkillsPicker from 'components/shared/EscoSkillsPicker/Index';
import { JobPostRequiredSkill } from 'core/interface/escoSkill';
import { jobPostSkillsToApiPayload } from 'utils/jobPostSkills';
import { CompetencyTag, CompetencyContainer } from 'app/publicJobs';
import colors from 'assets/variables/colors';
import {
  EditableShell,
  useInlineEditHint
} from 'components/companyJobDetail/inlineFields';

type InlineSkillsProps = {
  skills: JobPostRequiredSkill[];
  labels: string[];
  jobTitle?: string;
  disabled?: boolean;
  onSave: (skills: JobPostRequiredSkill[]) => Promise<boolean>;
};

const InlineSkills: FC<InlineSkillsProps> = ({
  skills,
  labels,
  jobTitle,
  disabled,
  onSave
}) => {
  const { t } = useLocales();
  const hint = useInlineEditHint();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<JobPostRequiredSkill[]>(skills);

  useEffect(() => {
    if (!editing) {
      setDraft(skills);
    }
  }, [skills, editing]);

  const handleSave = async () => {
    const ok = await onSave(draft);
    if (ok) {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="company-job-detail__editable is-editing">
        <EscoSkillsPicker
          value={draft}
          onChange={setDraft}
          jobTitle={jobTitle}
          initialSavedSkills={skills}
        />
        <div className="company-job-detail__edit-actions">
          <Button size="small" onClick={() => setEditing(false)}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={disabled}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
    >
      {labels.length ? (
        <CompetencyContainer style={{ marginTop: 0, marginBottom: 0 }}>
          {labels.map((skill) => (
            <CompetencyTag
              key={skill}
              tenantColor={colors.tenantColor}
              lighterPurple={colors.lightPurple}
            >
              {skill}
            </CompetencyTag>
          ))}
        </CompetencyContainer>
      ) : (
        <span className="company-job-detail__placeholder">
          {hint}
        </span>
      )}
    </EditableShell>
  );
};

export default InlineSkills;
export { jobPostSkillsToApiPayload };
